import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";
import { InAndOutDatetime, ResidentStatus } from "@/commons/services/Residents";
import { Template } from "@/commons/services/AdmissionApplicationTemplates";
import { Template as JdgTemplate } from "@/commons/services/AdmissionJudgementTemplates";

/**
 * ### 待機者 API 操作クラス
 */
export default class Waiters {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET Waiter
   */
  public async get(userStore: LoginUser, facilityId: number, residentId: number): Promise<WaitersGetModel> {
    const formatParams: any = {
      facilityId: facilityId,
      residentId: residentId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Waiter, formatParams);
    const response = await this.commonService.executeGet<WaitersGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET Waiters
   */
  public async gets(userStore: LoginUser, queryParams?: { startIndex?: number; endIndex?: number }): Promise<Array<WaitersGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Waiters, formatParams);
    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<Array<WaitersGetModel>>(userStore, requestUrl, this.constructor.name, params, HitomeqErrorType.IGNORE, 0);

    return response.data;
  }

  /**
   * ### GET WaitersIdInSystem
   *
   */
  public async getsByIdInSystem(userStore: LoginUser, residentIdInSystem: number): Promise<Array<WaitersGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      residentIdInSystem: residentIdInSystem
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.WaitersByIdInSystem, formatParams);
    const response = await this.commonService.executeGet<Array<WaitersGetModel>>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET WaitersByOtherFacilities
   */
  public async getsByOtherFacilities(userStore: LoginUser, queryParams?: { startIndex?: number; endIndex?: number }): Promise<Array<WaitersGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.WaitersByOtherFacilities, formatParams);
    const response = await this.commonService.executeGet<Array<WaitersGetModel>>(userStore, requestUrl, this.constructor.name, queryParams, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET WaitersCount
   */
  public async getCount(userStore: LoginUser): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Waiters, formatParams);
    const response = await this.commonService.executeGet<{ count: number }>(userStore, `${requestUrl}/count`, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.count;
  }

  /**
   * ### GET WaitersByOtherFacilitiesCount
   */
  public async getsByOtherFacilitiesCount(userStore: LoginUser): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.WaitersByOtherFacilities, formatParams);
    const response = await this.commonService.executeGet<{ count: number }>(userStore, `${requestUrl}/count`, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.count;
  }

  /**
   * ### GET getWaiterRegisterUrl
   */
  public async getWaiterRegisterUrl(userStore: LoginUser): Promise<string> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.WaiterRegisterUrl, formatParams);
    const response = await this.commonService.executeGet<{ url: string }>(userStore, `${requestUrl}`, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.url;
  }

  /**
   * ### POST Waiters
   */
  public async post(userStore: LoginUser, requestBody: WaitersPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Waiters, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### PATCH Waiters
   */
  public async patch(userStore: LoginUser, facilityId: number, residentId: number, requestBody: WaitersPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: facilityId,
      residentId: residentId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Waiter, formatParams);
    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### DELETE Waiters
   */
  public async delete(userStore: LoginUser, facilityId: number, residentId: number, requestBody?: WaitersDeleteModel): Promise<any> {
    const formatParams: any = {
      facilityId: facilityId,
      residentId: residentId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Waiter, formatParams);
    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0, requestBody);

    return response.data;
  }
}

export interface WaitersGetModel {
  /** 施設ID */
  facilityId: number;
  /** 利用者ソートキー({id}_{familyId}) */
  residentsSortKey: string;
  /** 施設で一意の利用者ID */
  id: number;
  /** 家族ID(0固定) */
  familyId: number;
  /** システムで一意の利用者ID */
  idInSystem: number;

  /** 状態(待機者画面ではステップと呼称している) */
  status: ResidentStatus;

  /**
   * 待機者情報
   * familyId == 0で登録される待機者
   */
  /** 利用者名(待機者名) */
  name: string;
  /** 電話番号 */
  tel: string;
  /** 生年月日 */
  birthday: number;
  /** 性別 */
  gender: string;
  /** パスワード */
  password: string;
  /** グループID(待機者はnull) */
  groupId: number;
  /** 部屋ID(待機者はnull) */
  roomId: number;
  /** 家族数 */
  familyCount: number;
  /** 待機者情報 */
  waiter: Waiter;

  /**
   * 待機者家族情報
   * familyId !=0 で登録される家族ユーザー
   */
  /** 家族名 */
  familyName: string;
  /** 家族続柄 */
  familyRelation: string;

  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 最終更新日 */
  updated: number;
  /** 作成した日付(YYYYMMDDhhmmssSSS) */
  created: number;
}

export interface Waiter {
  /** 変更申請中か否か(申請中：true、申請中ではない：false) */
  applyingForChange: boolean;
  /** 取り下げ申請中か否か(申請中：true、申請中ではない：false) */
  applyingForCancel: boolean;
  /** 取り下げ申請した申込者の施設ID */
  applicantFacilityId?: number;
  /** 取り下げ申請した申込者のresidentsSortKey */
  applicantSortKey?: string;
  /** 取り下げ理由 */
  cancelReason: string;
  /** 入所申込情報 */
  admissionApplication: AdmissionApplication;
  /** 入所評価判定情報 */
  admissionJudgement: AdmissionJudgement;
  /** 待機者ラベル */
  labels: number[];
}

export interface AdmissionApplication {
  /** 使用したテンプレートマスタ(admissionApplicationTemplates)の ID */
  templateId: number;
  /** 説明文 */
  information: string | undefined;
  /** テンプレートの内容、回答を保持する */
  templateAnswer: Template;
  /** 最終回答日時 */
  updated: number;
  /** 回答日時 */
  created: number;
}

export interface AdmissionJudgement {
  /** 使用したテンプレートマスタ(admissionJudgementTemplates)の ID */
  templateId: number;
  /** 説明文 */
  information: string | undefined;
  /** テンプレートの内容、回答を保持する */
  templateAnswer: JdgTemplate;
  /** 判定済みか否か */
  judged: boolean;
  /** 最終回答日時 */
  updated: number;
  /** 回答日時 */
  created: number;
}

export interface WaitersPostModel {
  /** 入所申込情報 */
  admissionApplication: PostAdmissionApplication;
  /** 利用者状態 */
  status: ResidentStatus;
  /** 申込日 */
  created: number;
  /** ラベル */
  labels: Array<number>;
}

export interface PostAdmissionApplication {
  /** 使用したテンプレートマスタ(admissionApplicationTemplates)の ID */
  templateId: number;
  /** 説明文 */
  information: string | undefined;
  /** テンプレートの内容、回答を保持する */
  templateAnswer: Template;
}

export interface WaitersPatchModel {
  /** 他施設の待機者を更新する場合に使用する、対象施設ID */
  targetFacilityId?: number;
  /** 利用者状態。更新する場合のみ格納する */
  status?: ResidentStatus;
  /** 利用者情報。更新する場合のみ格納する */
  resident?: UpdateResidentModel;
  /** 待機者情報 */
  waiter: Waiter;
  /** 同時登録する施設ID */
  concurrentRegisterFacilities?: Array<number>;

  /** 取り下げ申請受理（true）／不受理（false） */
  applyingForCancel?: boolean;
  /** 取り下げ申請受理／不受理メッセージタイトル */
  title?: string;
  /** 取り下げ申請受理／不受理メッセージ本文 */
  text?: string;

  /** 更新日時 */
  updated: number;
  /** 更新種別 */
  updateType: UpdateType;
}

export enum UpdateType {
  /** ステータス変更 */
  STATUS = "status",
  /** ステータス変更 */
  LABEL = "label",
  /** 取り下げ申請 */
  APPLYING_FOR_CANCEL = "applyingForCancel",
  /** 入居申込み */
  WAITER_ADMISSION_APPLICATION = "admission",
  /** 入居判定 */
  WAITER_JUDGEMENT_APPLICATION = "judgement"
}

export interface UpdateResidentModel {
  /** 部屋ID */
  roomId: number;
  /** グループID */
  groupId: number;
  /** 保険者番号 */
  insuredNumber: string | null;
  /** 介護保険被保険者番号 */
  careInsuranceInsuredNumber: string | null;
  /** 入退所情報 */
  inAndOutDatetimes: Array<InAndOutDatetime>;
}

export interface WaitersDeleteModel {
  /** 削除メッセージタイトル */
  deleteMessageTitle: string;
  /** 削除メッセージ本文 */
  deleteMessageText: string;
  /** 待機者家族削除時に使用する、同時削除対象の施設ID */
  concurrentDeleteFacilityIds: number[];
}
